/*
 * Copyright (c) 2020 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, July 2020
 */

import React, { useCallback, useContext, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FlowBlockNavigationBar as BlockModel } from '@staffino/model';
import {
  Box,
  Button,
  Image,
  Paragraph,
  styled,
  Theme,
} from '@staffino/primitive-controls';
import useObjects from '../use-objects-hook';
import { LocalePicker } from '../controls/locale-picker';
import useBridge from '../use-bridge-hook';
import usePageStateManager from '../use-page-state-manager-hook';
import FlowContext from '../flow-context';

type BarContainerProps = {
  theme?: Theme;
};

const BarContainer = styled(Box)<BarContainerProps>((props) => ({
  borderTop: `5px solid ${props.theme.colors.primary}`,
}));

const RestartButton = styled(Button)({
  paddingLeft: '1rem',
  paddingRight: '1rem',
});

type Props = {
  block: BlockModel;
};

const NavigationBarSection = Box;

const NavigationBarBlock = ({ block }: Props) => {
  const bridge = useBridge();
  const pageStateManager = usePageStateManager();
  const { t } = useTranslation();
  const [lineage] = useObjects('target-lineage', { id: bridge.targetId });
  const locales = useObjects('locale');
  const { stateManager } = useContext(FlowContext);

  const sortedLocales = useMemo(() => {
    const cloned = [...(locales ?? [])];
    cloned.sort((a, b) => a.name.localeCompare(b.name));
    return cloned;
  }, [locales]);
  const handleSelect = useCallback(
    async (locale) => {
      try {
        return await bridge.setLocale(locale.id, locale.code);
      } catch (error) {
        pageStateManager.addNotification(
          'info',
          t('Connection lost. Could not perform this action.'),
          5,
        );
      }
      return undefined;
    },
    [bridge, pageStateManager, t],
  );
  const activeLocale = locales && locales.find((l) => l.id === bridge.localeId);
  const imageUrl = block.imageUrl || lineage?.targets?.[0]?.imageUrl;
  const title = block.title || lineage?.targets?.[0]?.name;

  return (
    <BarContainer
      flexDirection={'row' as const}
      alignItems="center"
      py={2}
      minHeight="3.5rem"
    >
      {imageUrl && (
        <Image
          source={{ uri: imageUrl, width: 38, height: 38 }}
          borderRadius="50%"
          border="solid 1px rgba(163, 178, 191, 0.2)"
          ml="0.625rem"
        />
      )}
      {title && (
        <Paragraph fontWeight="bold" ml="1rem" color="headline">
          {title}
        </Paragraph>
      )}

      {activeLocale && locales?.length > 1 && (
        <NavigationBarSection ml="auto">
          <LocalePicker
            activeLocale={activeLocale}
            locales={sortedLocales}
            onSelect={handleSelect}
          />
        </NavigationBarSection>
      )}

      {block?.withRestartButton && (
        <Box margin="0 1rem 0 0">
          <RestartButton
            bg="#007bff"
            onPress={() => stateManager.restartFlow()}
          >
            <Trans>Restart survey</Trans>
          </RestartButton>
        </Box>
      )}
    </BarContainer>
  );
};

export default NavigationBarBlock;
